import type { League, LeagueDivision } from './season-results';

export const SEASON_THREE_ID = 'S3';
export const SEASON_THREE_NAME = 'Season 3';
export const SEASON_THREE_LEAGUE_ID = 'DFL';
export const SEASON_THREE_LEAGUE_NAME = 'MF League';
export const SEASON_THREE_START_DATE = '2026-09-30';
export const SEASON_THREE_REGULAR_SEASON_DAYS = 15;
export const SEASON_THREE_PLAYOFF_DATES = ['2026-10-15', '2026-10-16', '2026-10-17', '2026-10-18'] as const;
export const SEASON_THREE_ROUNDS_PER_DAY = 3;
export const SEASON_THREE_FIRST_KICKOFF_UTC_HOUR = 21; // 4:00 PM Central (CDT)
export const SEASON_THREE_GAMES_PER_KICKOFF_SLOT = 3;
export const SEASON_THREE_KICKOFF_INTERVAL_MINUTES = 20;
export const SEASON_THREE_PLAYOFF_TEAMS = 12 as const;

export type SeasonThreeFranchisePreset = {
  tokenId: string;
  city: string;
  name: string;
  abbr: string;
  assignedWallet: string;
};

/**
 * Confirmed Season 3 franchise identities, keyed to the newly minted token ids.
 * The first twelve preserve the already-distributed token assignments. The
 * remaining twelve occupy the next sequential token ids as they are minted.
 */
export const SEASON_THREE_FRANCHISE_PRESETS: readonly SeasonThreeFranchisePreset[] = [
  { tokenId: '13', city: 'Boise', name: 'Bison', abbr: 'BOI', assignedWallet: '0x9e644dBe35F07E6C0273f8bD288bb536E3A37ceB' },
  { tokenId: '14', city: 'San Diego', name: 'Sharks', abbr: 'SDS', assignedWallet: '0x0CB599da51D1D26FF225935312eF714f4dF2E021' },
  { tokenId: '15', city: 'Los Angeles', name: 'Degens', abbr: 'LAD', assignedWallet: '0x9723C0e3B3a5AC2F3Dc8d08ed278A31eA5E43D27' },
  { tokenId: '16', city: 'Sactown', name: 'Brawlers', abbr: 'SAC', assignedWallet: '0x9723C0e3B3a5AC2F3Dc8d08ed278A31eA5E43D27' },
  { tokenId: '17', city: 'Midwest', name: 'Magic', abbr: 'MWM', assignedWallet: '0x73164a96e0e07fd2fc1fdb486165eb80fd7becca' },
  { tokenId: '18', city: 'Boston', name: 'Bad Moons', abbr: 'BBM', assignedWallet: '0x73164a96e0e07fd2fc1fdb486165eb80fd7becca' },
  { tokenId: '19', city: 'Florida', name: 'Leos', abbr: 'FLA', assignedWallet: '0x40BFD28e2e712ca7523EB75e4DF8753E68Dd1aef' },
  { tokenId: '20', city: 'Fort Pierce', name: 'Farmers', abbr: 'FPF', assignedWallet: '0x098F1925660FaFF87AA134b15B4032f484CBc079' },
  { tokenId: '21', city: 'Ashland', name: 'Runescapes', abbr: 'ASR', assignedWallet: '0x0ec176BB1C52097c110f6FbbC24E582437883C11' },
  { tokenId: '22', city: 'Ashland', name: 'Outlawz', abbr: 'ASO', assignedWallet: '0xeea08913625F2298A4616F84ddE1F832ffff2Cac' },
  { tokenId: '23', city: 'Hudson River', name: 'Whalers', abbr: 'HRW', assignedWallet: '0x9f22E6cA92aaD17176fa288f6a0652CF07fc45F3' },
  { tokenId: '24', city: 'Long Island', name: 'Mariners', abbr: 'LIM', assignedWallet: '0x83f4b3ee67a36ab34a849bc018070a2e9e1d904b' },
  { tokenId: '25', city: 'Denver', name: 'Dimes', abbr: 'DEN', assignedWallet: '0x58Ad4856C7561C6CdB924b1286E64Ab350b7A3a8' },
  { tokenId: '26', city: 'Las Vegas', name: 'Vipers', abbr: 'LVV', assignedWallet: '0xb7e6eeBc5D20c32D1E97e0CA748E48E59d8010c6' },
  { tokenId: '27', city: 'Dallas', name: 'Drifters', abbr: 'DAL', assignedWallet: '0x2a79421e5bd8676889b77905628285ca9ea38be8' },
  { tokenId: '28', city: 'Oregon', name: 'Lumberjacks', abbr: 'ORL', assignedWallet: '0x6A5d9f933d2d9eFC5108708bBD970936867fE019' },
  { tokenId: '29', city: 'Rocky Mount', name: 'Maulers', abbr: 'RMM', assignedWallet: '0xEee94eeFDbf0522fd36Dd3A344483F6403BF6F0e' },
  { tokenId: '30', city: 'Bay City', name: 'Sugar Beats', abbr: 'BCS', assignedWallet: '0x456cc502F132707d9c7E20f23c879404a7822186' },
  { tokenId: '31', city: 'Appleton', name: 'Admirals', abbr: 'APP', assignedWallet: '0x6Ea3c2E9050649954A4cfdcEE3e3d2034b0EbE38' },
  { tokenId: '32', city: 'Toronto', name: 'Wolfpack', abbr: 'TOR', assignedWallet: '0x5105a549e8C7a9E93C91Bb488f98c0edc75C0727' },
  { tokenId: '33', city: 'Bay City', name: 'Cougars', abbr: 'BCC', assignedWallet: '0x5C96397BFC9e5E52F4A6eCA7D48553BAd188a3D5' },
  { tokenId: '34', city: 'Pennsylvania', name: 'Coal Miners', abbr: 'PCM', assignedWallet: '0x661D62AB03e0F4fb9A4Ded9baC056A9d82b182b8' },
  { tokenId: '35', city: 'Buffalo', name: 'Bullies', abbr: 'BUF', assignedWallet: '0xB02aB37F08EbD4c71792c452F70f66742e46c1e7' },
  { tokenId: '36', city: 'Philadelphia', name: 'Stars', abbr: 'PHI', assignedWallet: '0x04295BA1d19e601d69f4bbdB9507F21E8590b8c1' },
] as const;

const SEASON_THREE_FRANCHISE_PRESET_BY_ID = new Map(
  SEASON_THREE_FRANCHISE_PRESETS.map((preset) => [preset.tokenId, preset]),
);

export function applySeasonThreeFranchisePreset<T extends { id?: string; franchiseId?: string; city: string; name: string; abbr: string }>(team: T): T {
  const id = String(team.franchiseId || team.id || '');
  const preset = SEASON_THREE_FRANCHISE_PRESET_BY_ID.get(id);
  return preset ? { ...team, city: preset.city, name: preset.name, abbr: preset.abbr } : team;
}

export function seasonThreeAssignedWallet(teamId: string): string | null {
  return SEASON_THREE_FRANCHISE_PRESET_BY_ID.get(String(teamId))?.assignedWallet ?? null;
}

export function seasonThreeAssignedTeamIds(wallet: string): string[] {
  const target = String(wallet).toLowerCase();
  return SEASON_THREE_FRANCHISE_PRESETS
    .filter((preset) => preset.assignedWallet.toLowerCase() === target)
    .map((preset) => preset.tokenId);
}

/** The 24 Team NFT token ids participating in Season 3. */
export const SEASON_THREE_TEAM_IDS = Array.from({ length: 24 }, (_, index) => String(index + 13));

export const SEASON_THREE_DIVISION_MEMBERSHIPS = [
  { name: 'West', teamIds: ['13', '25', '14', '15', '16', '17'] },
  { name: 'South', teamIds: ['26', '27', '19', '28', '29', '20'] },
  { name: 'North', teamIds: ['30', '31', '21', '18', '32', '22'] },
  { name: 'East', teamIds: ['33', '34', '35', '23', '24', '36'] },
] as const;

export function buildSeasonThreeLeague(): League {
  const divisions: LeagueDivision[] = SEASON_THREE_DIVISION_MEMBERSHIPS.map((division, index) => ({
    id: `${SEASON_THREE_LEAGUE_ID}-D${index + 1}`,
    name: division.name,
    teamIds: [...division.teamIds],
  }));

  return {
    id: SEASON_THREE_LEAGUE_ID,
    name: SEASON_THREE_LEAGUE_NAME,
    playoffTeamCount: SEASON_THREE_PLAYOFF_TEAMS,
    conferences: [{ id: SEASON_THREE_LEAGUE_ID, name: SEASON_THREE_LEAGUE_NAME, divisions }],
  };
}
